import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import AnimatedSection from 'src/components/AnimatedSection'
import { PILLAR_NS_MAP, PillarPage } from 'src/constants/searchPages'

interface IProps {
  pillar: PillarPage
  ctaHref: string
}

interface IFeatureCard {
  picture: string
  alt: string
  title: string
  subtitle: string
}

const Features = ({ pillar, ctaHref }: IProps) => {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tStats } = useTranslation('platform_stats')
  const { t: tCommon } = useTranslation('common')
  const cards: IFeatureCard[] = t('FEATURES.ITEMS', {
    returnObjects: true,
    cities: tStats('CITIES')
  })

  return (
    <AnimatedSection
      className="w-full bg-white-500 py-20 md:py-28"
      variant="slideUp"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
          {/* Editorial header rail */}
          <div className="md:col-span-4 md:pt-2">
            <p className="font-display text-sm tracking-[0.2em] text-blue-500 uppercase">
              {tCommon('PILLAR_SECTIONS.EYEBROW_BENEFITS')}
            </p>
            <h2 className="mt-4 font-display text-4xl leading-[1.05] font-medium text-ink-700 md:text-5xl">
              {t('FEATURES.TITLE')}
            </h2>
            <p className="mt-5 max-w-sm text-base leading-relaxed text-ink-200">
              {t('FEATURES.SUBTITLE')}
            </p>
            <Link
              href={ctaHref}
              className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-500 transition-colors hover:text-blue-700"
            >
              {t('FEATURES.BUTTON_PHOLDER')}
              <ArrowForwardIcon className="text-base transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Features — 2-column editorial grid on desktop, stacked on mobile */}
          <ul className="grid grid-cols-1 gap-x-10 gap-y-10 sl:grid-cols-2 md:col-span-8 md:gap-y-12">
            {cards.map((card, index) => (
              <li
                key={`feature-${index}`}
                className="relative flex flex-col gap-3 border-t border-ink-100/20 pt-6"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-sm font-medium text-blue-500 italic">
                    0{index + 1}
                  </span>
                  <div className="relative size-8 opacity-80">
                    <Image
                      src={card.picture}
                      alt={card.alt}
                      fill
                      sizes="32px"
                      style={{ objectFit: 'contain' }}
                      loading="lazy"
                    />
                  </div>
                </div>
                <h3 className="mt-1 font-display text-xl leading-[1.2] font-medium text-ink-700 md:text-2xl">
                  {card.title}
                </h3>
                <p className="text-[15px] leading-relaxed text-ink-200">
                  {card.subtitle}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </AnimatedSection>
  )
}

export default React.memo(Features)
